import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { authenticatedFetch } from '../utils';

/**
 * Fetch tenant settings (integrations, notifications, etc.).
 */
export const useSettingsQuery = (enabled = true) => {
  return useQuery({
    queryKey: ['settings'],
    queryFn: async () => {
      const response = await authenticatedFetch('/api/tenant/settings');
      if (!response.ok) throw new Error('Failed to fetch settings');
      const data = await response.json();
      return data.settings || {
        salestrailApiKey: '',
        salestrailConfig: {},
        mobilecrmApiKey: '',
        mobilecrmConfig: {},
        otherSettings: {},
      };
    },
    staleTime: 5 * 60 * 1000,
    enabled,
  });
};

/**
 * Mutation hook for updating settings.
 * Automatically invalidates the settings cache on success.
 */
export const useUpdateSettings = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (newSettings) => {
      // POST, not PUT. The backend only registers @app.post for this path, so
      // every settings save was a 405 that surfaced as a generic failure.
      const response = await authenticatedFetch('/api/tenant/settings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newSettings),
      });
      if (!response.ok) {
        // Carry the server's own words: "admin only" and "connection issue" need
        // different responses from the user, and a generic string hides both.
        let detail = '';
        try {
          const body = await response.json();
          detail = body?.detail || body?.message || '';
        } catch {
          /* a non-JSON error body is not worth a second failure */
        }
        throw new Error(detail || `Could not save settings (${response.status})`);
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] });
    },
  });
};
